import React, { useRef, useEffect, useState } from 'react';
import { Calendar, Wrench, ClipboardCheck, HardHat } from 'lucide-react';

// Auto-scroll settings
const SCROLL_SPEED = 30; // pixels per second
const PAUSE_AT_ENDS = 3000; // 3 second pause at top/bottom

const typeConfig = {
  calibration: {
    icon: Calendar,
    label: 'Kalibrointi',
    color: '#3b82f6'
  },
  inspection: {
    icon: ClipboardCheck,
    label: 'Katsastus',
    color: '#10b981'
  },
  mining_inspection: {
    icon: HardHat,
    label: 'Kaivoskatsastus',
    color: '#f59e0b'
  },
  planned_repair: {
    icon: Wrench,
    label: 'Huolto',
    color: '#8b5cf6'
  }
};

const formatDaysUntil = (days) => {
  if (days === 0) return 'Tänään';
  if (days === 1) return 'Huomenna';
  if (days === -1) return 'Eilen';
  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays < 7) return `${absDays} pv sitten`;
    const weeks = Math.floor(absDays / 7);
    return weeks === 1 ? '1 vko sitten' : `${weeks} vko sitten`;
  }
  if (days < 7) return `${days} pv`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? '1 vko' : `${weeks} vko`;
};

const UpcomingWidget = ({ events = [], fontScale = 'medium', compact = false, customScale = 1.0, autoScroll = true }) => {
  const scrollRef = useRef(null);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [isPaused, setIsPaused] = useState(true);

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScroll || compact || !events || events.length === 0) return;

    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let lastTime = performance.now();
    let pauseTimeout;

    const checkOverflow = () => {
      return scrollContainer.scrollHeight > scrollContainer.clientHeight;
    };

    const scroll = (currentTime) => {
      if (!checkOverflow()) {
        animationId = requestAnimationFrame(scroll);
        return;
      }

      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      if (!isPaused) {
        const scrollAmount = SCROLL_SPEED * deltaTime;
        const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;

        if (scrollDirection === 'down') {
          scrollContainer.scrollTop += scrollAmount;
          if (scrollContainer.scrollTop >= maxScroll) {
            scrollContainer.scrollTop = maxScroll;
            setIsPaused(true);
            pauseTimeout = setTimeout(() => {
              setScrollDirection('up');
              setIsPaused(false);
            }, PAUSE_AT_ENDS);
          }
        } else {
          scrollContainer.scrollTop -= scrollAmount;
          if (scrollContainer.scrollTop <= 0) {
            scrollContainer.scrollTop = 0;
            setIsPaused(true);
            pauseTimeout = setTimeout(() => {
              setScrollDirection('down');
              setIsPaused(false);
            }, PAUSE_AT_ENDS);
          }
        }
      }

      animationId = requestAnimationFrame(scroll);
    };

    // Start with a pause at top
    pauseTimeout = setTimeout(() => {
      setIsPaused(false);
    }, PAUSE_AT_ENDS);

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (pauseTimeout) clearTimeout(pauseTimeout);
    };
  }, [autoScroll, compact, events, scrollDirection, isPaused]);

  // Perusfontit (vw-yksiköissä)
  const baseSizes = {
    title: 1.2,   // vw
    item: 0.9,    // vw
    days: 0.85,   // vw
  };

  // Skaalaa kaikki fontit customScale-kertoimella
  const scale = customScale;
  const sizes = {
    title: `${baseSizes.title * scale}vw`,
    item: `${baseSizes.item * scale}vw`,
    days: `${baseSizes.days * scale}vw`,
  };

  if (!events || events.length === 0) {
    return (
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        border: '1px solid #334155',
        padding: '1rem',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          fontWeight: 'bold',
          fontSize: sizes.title,
          color: '#f1f5f9',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Calendar size={18} />
          AJANKOHTAISTA
        </div>
        <div style={{
          color: '#64748b',
          fontSize: sizes.item,
          textAlign: 'center',
          marginTop: 'auto',
          marginBottom: 'auto'
        }}>
          Ei ajankohtaisia tapahtumia
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: compact ? 'transparent' : '#1e293b',
      borderRadius: compact ? 0 : '12px',
      border: compact ? 'none' : '1px solid #334155',
      padding: compact ? '0.5rem' : '1rem',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header - hidden in compact mode */}
      {!compact && (
        <div style={{
          fontWeight: 'bold',
          fontSize: sizes.title,
          color: '#f1f5f9',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexShrink: 0
        }}>
          <Calendar size={18} />
          AJANKOHTAISTA
        </div>
      )}

      {/* Events list */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '0.4rem' : '0.5rem',
          overflow: 'hidden',
          flex: 1
        }}
      >
        {events.map((event, index) => {
          const config = typeConfig[event.type] || typeConfig.calibration;
          const Icon = config.icon;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                backgroundColor: '#0f172a',
                borderRadius: '6px',
                borderLeft: `3px solid ${config.color}`
              }}
            >
              <Icon size={16} style={{ color: config.color, flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: sizes.item,
                  color: '#f1f5f9',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {event.device_name}
                </div>
                <div style={{
                  fontSize: sizes.days,
                  color: '#94a3b8'
                }}>
                  {config.label}
                  {event.description && ` - ${event.description}`}
                </div>
              </div>

              <div style={{
                fontSize: sizes.days,
                fontWeight: 'bold',
                color: event.days_until < 0 ? '#ef4444' : event.days_until <= 3 ? '#f97316' : event.days_until <= 7 ? '#f59e0b' : '#10b981',
                flexShrink: 0
              }}>
                {formatDaysUntil(event.days_until)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingWidget;
